import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import MessageChatCircleIcon from "@redotech/redo-icons/message-chat-circle.svg";
import { useEscalationContext } from "@redotech/redo-merchant-app-common/escalation";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { AiChatContext } from "@redotech/redo-web/ai/chat/contexts/ai-chat-context";
import { ToolComponentProps } from "@redotech/redo-web/ai/chat/types/tool-component-props";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoIcon } from "@redotech/redo-web/arbiter-components/icon/redo-icon";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, ReactNode, useContext } from "react";
import * as styles from "./escalate-to-human-tool.module.css";
import { useInChatEscalation } from "./use-in-chat-escalation";

interface EscalationCardProps {
  title: string;
  description: string;
  error: string | null;
  children: ReactNode;
}

const EscalationCard = memo(function EscalationCard({
  title,
  description,
  error,
  children,
}: EscalationCardProps) {
  return (
    <Flex
      align="center"
      bgColor="primary"
      borderColor="secondary"
      borderStyle="solid"
      borderWidth="1px"
      dir="column"
      gap="md"
      m="lg"
      p="lg"
      radius="md"
      shadow="lg"
    >
      <Flex align="center" dir="column" gap="sm">
        <Flex align="center" className={styles.iconWrapper} justify="center">
          <RedoIcon color="brand" Icon={MessageChatCircleIcon} size={20} />
        </Flex>

        <Text fontSize="md" fontWeight="semibold" textAlign="center">
          {title}
        </Text>

        <Text color="secondary" fontSize="sm" textAlign="center">
          {description}
        </Text>
      </Flex>

      {error && (
        <Text color="error" fontSize="xs" textAlign="center">
          {error}
        </Text>
      )}

      {children}
    </Flex>
  );
});

export function EscalateToHumanTool(props: ToolComponentProps) {
  const { sendToolResponse, messages } = useContext(AiChatContext);
  const merchantRpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const { triageThreadId, navigateToThread, escalation } =
    useEscalationContext();

  const activeEscalationLoad = useLoad(
    async (signal) => {
      const result = await merchantRpcClient.checkActiveEscalation(
        {},
        { signal },
      );
      return result.activeEscalation;
    },
    [merchantRpcClient],
  );

  const activeEscalation = activeEscalationLoad.value;
  const hasExistingEscalationOnDifferentThread =
    activeEscalation != null &&
    activeEscalation.triageThreadId !== triageThreadId;
  const hasExistingEscalationOnCurrentThread =
    activeEscalation != null &&
    activeEscalation.triageThreadId === triageThreadId;

  const sendEscalationResponse = useHandler(() => {
    void sendToolResponse(
      {
        toolCallId: props.toolCallId,
        toolName: props.toolName,
        result: { output: "Escalation started" },
      },
      { skipLlmCall: true },
    );
  });

  const { escalate, isLoading, error, isEscalated } = useInChatEscalation({
    messages,
    input: props.input,
    onSuccess: sendEscalationResponse,
  });

  const handleSameIssue = useHandler(() => {
    if (activeEscalation && navigateToThread) {
      navigateToThread(activeEscalation.triageThreadId);
      void sendToolResponse(
        {
          toolCallId: props.toolCallId,
          toolName: props.toolName,
          result: { output: "Navigated to existing escalation" },
        },
        { skipLlmCall: true },
      );
    }
  });

  if (activeEscalationLoad.pending) {
    return null;
  }

  if (props.output !== undefined) {
    return null;
  }

  if (
    props.disabled ||
    escalation.status !== "idle" ||
    hasExistingEscalationOnCurrentThread ||
    isEscalated
  ) {
    return null;
  }

  if (hasExistingEscalationOnDifferentThread) {
    return (
      <EscalationCard
        description="Would you like to continue your existing conversation?"
        error={error}
        title="You already have an open conversation with the team"
      >
        <Flex gap="md">
          <RedoButton
            className={styles.button}
            disabled={props.disabled || !navigateToThread}
            hierarchy="primary"
            onClick={handleSameIssue}
            size="sm"
            text="Yes"
          />
          <RedoButton
            className={styles.button}
            disabled={isLoading || props.disabled}
            hierarchy="secondary"
            onClick={escalate}
            pending={isLoading}
            size="sm"
            text={isLoading ? "Connecting..." : "No"}
          />
        </Flex>
      </EscalationCard>
    );
  }

  return (
    <EscalationCard
      description="A team member will join the conversation to help."
      error={error}
      title="Would you like to connect with our team?"
    >
      <RedoButton
        className={styles.button}
        disabled={isLoading || props.disabled}
        hierarchy="primary"
        onClick={escalate}
        pending={isLoading}
        size="sm"
        text={isLoading ? "Connecting..." : "Yes, connect me"}
      />
    </EscalationCard>
  );
}
